import type React from 'react'
import type { JSX } from 'react'
import type { IEpisode, IPlaylistProps, ISeason } from './Playlist.h'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ButtonFonts, ButtonIconSizes, IconPositions } from 'atoms/Button'
import { GhostButtonTemplate } from 'atoms/Button/Templates'
import { IconNames } from 'atoms/Icon/Icon.h'
import { OverlayLayoutColors } from 'atoms/OverlayLayout'
import { PopupUnderLayer } from 'atoms/PopupUnderLayer'
import { CARDS_ASPECT_RATIO } from 'blocks/Cards'
import { CORNERS, SHADOW } from 'vars/index'
import { PlaylistPopoverContent } from './components/PlaylistPopoverContent'
import { PlaylistSkeleton } from './components/PlaylistSkeleton'
import { usePlaylistPopoverControls } from './hooks'
import {
  getActiveEpisode,
  getActiveSeason,
  isActiveTabEpisodes,
  isActiveTabSeasons,
  scrollToContentItem,
} from './lib'
import { PlaylistPopoverMenuItems } from './Playlist.h'
import {
  PlaylistPopoverHeaderSC,
  PlaylistPopUpSC,
  PlaylistScrollableBlockSC,
  PopoverGradientOverlay,
  TitleSC,
} from './Playlist.styled'

const getInitialPlaylistState = ({
  seasonsWithEpisodes,
  episodes,
  currentEpisode,
}: {
  seasonsWithEpisodes?: ISeason[]
  episodes?: IEpisode[]
  currentEpisode?: number | null
}) => {
  const isSeasons = Boolean(seasonsWithEpisodes?.length)

  if (!currentEpisode) {
    return {
      activeTab: isSeasons
        ? PlaylistPopoverMenuItems.Seasons
        : PlaylistPopoverMenuItems.Default,
      activeSeason: undefined,
      activeEpisode: undefined,
    }
  }

  if (!isSeasons) {
    return {
      activeTab: PlaylistPopoverMenuItems.Default,
      activeSeason: undefined,
      activeEpisode: getActiveEpisode(episodes, currentEpisode),
    }
  }

  const activeSeason = getActiveSeason({ seasonsWithEpisodes, currentEpisode })

  return {
    activeTab: PlaylistPopoverMenuItems.Episodes,
    activeSeason,
    activeEpisode: getActiveEpisode(activeSeason?.episodes, currentEpisode),
  }
}

export const stopEvent = (
  event?: MouseEvent | KeyboardEvent | React.MouseEvent
) => {
  event?.stopPropagation()
  event?.preventDefault()
}

export const Playlist = ({
  isLoading = true,
  onClose = () => undefined,
  playlist = undefined,
  title = undefined,
  toggleClassName,
  shadow = SHADOW.NORMAL,
  corners = CORNERS.CORNERS_30,
  posterAspectRatio = CARDS_ASPECT_RATIO.HORIZONTAL_DEFAULT,
  posterGradient = OverlayLayoutColors.EPISODE_CARD_VERTICAL_OVERLAY,
  posterWidth = '160px',
}: IPlaylistProps): JSX.Element => {
  const { seasonsWithEpisodes, episodes, currentEpisode } = playlist || {}
  const initialState = getInitialPlaylistState({
    seasonsWithEpisodes,
    episodes,
    currentEpisode,
  })

  const [activeTab, setActiveTab] = useState<PlaylistPopoverMenuItems>(
    initialState.activeTab
  )
  const [activeSeason, setActiveSeason] = useState<ISeason | undefined>(
    initialState.activeSeason
  )
  const [activeEpisode, setActiveEpisode] = useState<IEpisode | undefined>(
    initialState.activeEpisode
  )
  const [isTopNotReached, setIsTopNotReached] = useState(false)
  const [isBottomNotReached, setIsBottomNotReached] = useState(false)

  const isSeasons = Boolean(seasonsWithEpisodes?.length)
  const popupBodyRef = useRef<HTMLDivElement>(null)

  const isTabSeasons = isActiveTabSeasons({ isSeasons, activeTab })

  const {
    handleScroll,
    handleEpisodeClick,
    handleSeasonClick,
    closePopover,
    returnToPrevTab,
  } = usePlaylistPopoverControls({
    setIsTopNotReached,
    setIsBottomNotReached,
    setActiveEpisode,
    activeSeason,
    setActiveSeason,
    isSeasons,
    setActiveTab,
    activeTab,
    playlist,
    onClose,
  })

  useEffect(() => {
    const popupBody = popupBodyRef.current
    setIsBottomNotReached(
      !!popupBody && popupBody.scrollHeight > popupBody.clientHeight
    )
  }, [popupBodyRef])

  useLayoutEffect(() => {
    if (activeEpisode && isActiveTabEpisodes({ isSeasons, activeTab })) {
      const contentItems = isSeasons ? activeSeason?.episodes : episodes
      scrollToContentItem<IEpisode>({
        contentItems,
        activeContentItem: activeEpisode,
        popupBodyRef,
      })
      return
    }
    if (isTabSeasons) {
      scrollToContentItem<ISeason>({
        contentItems: seasonsWithEpisodes,
        activeContentItem: activeSeason,
        popupBodyRef,
      })
    }
  }, [
    activeTab,
    activeSeason,
    isTabSeasons,
    activeEpisode,
    isSeasons,
    episodes,
    seasonsWithEpisodes,
  ])

  useEffect(() => {
    const nextState = getInitialPlaylistState({
      seasonsWithEpisodes,
      episodes,
      currentEpisode,
    })

    setActiveTab(nextState.activeTab)
    setActiveSeason(nextState.activeSeason)
    setActiveEpisode(nextState.activeEpisode)
  }, [currentEpisode, seasonsWithEpisodes, episodes])

  const headFont = ButtonFonts.FontLgBold

  return (
    <PopupUnderLayer
      onClose={onClose}
      toggleClassName={toggleClassName}
      position='absolute'
      top='0'
      left='0'
      right='0'
      bottom='0'
    >
      <PlaylistPopUpSC onClick={stopEvent} shadow={shadow} corners={corners}>
        {isLoading && <PlaylistSkeleton />}
        {!isLoading && (
          <>
            <PlaylistPopoverHeaderSC isTopNotReached={isTopNotReached}>
              {isTabSeasons && (
                <TitleSC>
                  <GhostButtonTemplate title={title} font={headFont} />
                </TitleSC>
              )}
              {!isTabSeasons && (
                <TitleSC>
                  <GhostButtonTemplate
                    title={activeSeason?.name}
                    font={headFont}
                    icon={IconNames.angleLeft}
                    iconSize={ButtonIconSizes.xSm}
                    onClick={returnToPrevTab}
                  />
                </TitleSC>
              )}
              <GhostButtonTemplate
                onClick={closePopover}
                icon={IconNames.close}
                iconPosition={IconPositions.Center}
                iconSize={ButtonIconSizes.mSm}
              />
            </PlaylistPopoverHeaderSC>
            <PopoverGradientOverlay
              isBottomNotReached={isBottomNotReached}
              onScroll={handleScroll}
            >
              <PlaylistScrollableBlockSC onScroll={handleScroll}>
                <PlaylistPopoverContent
                  playlist={playlist}
                  posterAspectRatio={posterAspectRatio}
                  posterWidth={posterWidth}
                  posterGradient={posterGradient}
                  handleSeasonClick={handleSeasonClick}
                  handleEpisodeClick={handleEpisodeClick}
                  isTabSeasons={isTabSeasons}
                  isSeasons={isSeasons}
                  activeSeason={activeSeason}
                  activeEpisode={activeEpisode}
                />
              </PlaylistScrollableBlockSC>
            </PopoverGradientOverlay>
          </>
        )}
      </PlaylistPopUpSC>
    </PopupUnderLayer>
  )
}
